/* eslint-disable max-lines */
/* eslint-disable no-restricted-syntax */

/*
 * GA4 / Segment Analytics Tracking Library
 * @jira https://nutrafol.atlassian.net/browse/NT2-29811
 * @doc Tracking Strategy: https://docs.google.com/presentation/d/1UgJtuHGz6M-zK2Uuc-8Ptda3wYIKWXY6xwl_y8Q9N6M/
 * @doc Event Dictionary: https://docs.google.com/spreadsheets/d/1mCegxI2QWCJUSdfqNrl4DI0PVzlgma-1ZYM5FsAfGsA/
 */

import { AtcProduct } from "@/types/atc";
import { CartData, CartItem } from "@/types/cart";
import { CartFragment, CheckoutItem } from "@/types/checkout";
import type { EventData } from "@/types/common";
import { ProductLine } from "@/types/common";
import {
  GA4option,
  ManyOptionsData,
  CartTotalsData,
  ViewItemListData,
  RibbonDataExtended,
  ProductSelection,
  PaymentMethodDeclined,
  CheckoutShippingUpdatedFunction,
  CheckoutShippingSubmitted,
  SingleProductData,
  AddressValidationShown,
  EmailMarketingOptedIn,
  ModalViewedData,
  VideoPlayedData,
  CarouselClickedData,
  NavigationClickedData,
  MarketingCtaClickedData,
  SearchData,
  SearchDataProduct,
} from "@/types/ga4";
// eslint-disable-next-line import/order
import {
  getCartState,
  getOrderCartBusinessLine,
  getOrderCartProductLine,
  getOrderCartState,
  getSegmentCheckoutProducts,
  getSegmentProducts,
  getSegmentQuizRecoItems,
  getCheckoutQuizRecoItems,
  getKlaviyoValues,
} from "@/utils/cart";

//import { pdpPathToSku } from "@/utils/pdpLinkHelper";

import { CART_STORAGE_KEYS, getCartStorage } from "@/utils/cartStorage";
import { readCookie } from "@/utils/cookies";
import { deriveProductLine } from "@/utils/deriveProductLine";
import { getItemReportingAttributes } from "@/utils/getItemReportingAttributes";
import { segmentEvent } from "@/utils/segmentfunctions";

export const getEventProductLine = (products: AtcProduct[]): ProductLine =>
  deriveProductLine(products, (p) =>
    p?.options?.map((o) => o?.product_gender_new ?? o?.product_gender)
  );

const getSafeItems = (items: Record<string, CartItem> | CheckoutItem[]) => {
  if (typeof items === "object") {
    return Object.values(items);
  }
  return [];
};

export const GA4 = {
  //RIBBON EVENTS

  onViewPromotion: (data: RibbonDataExtended) => {
    if (typeof sessionStorage === "undefined") {
      return;
    }
    const { copy, promoName } = data;
    const hasSeenPromotion = sessionStorage.getItem("ga4:ribbon:viewed");

    if (hasSeenPromotion) {
      // allowing ribbon to be fired once per session
      return;
    }
    segmentEvent("View Promotion", {
      location: "ribbon",
      promotion_id: promoName || "Regular",
      promotion_name: copy,
      creative_name: null,
      creative_slot: "home_top",
      promotion_type: "internal",
      products: [],
    });

    sessionStorage.setItem("ga4:ribbon:viewed", "true");
  },

  onSelectPromotion: (data: RibbonDataExtended) => {
    const { copy, href, promoName } = data;
    // const items = getItemsByHref(href);

    segmentEvent("Marketing CTA Clicked", {
      location: "ribbon",
      destination: href,
      text: copy,
      type: "ribbon",
      module_location: "ribbon",
      promotion_id: promoName || "Regular",
      promotion_name: copy,
      creative_name: null,
      creative_slot: "home_top",
      promotion_type: "internal",
      products: [],
    });
  },

  //PDP EVENTS

  onViewItemList: (data: ViewItemListData) => {
    const {
      products: productsRaw,
      location: locationProp,
      module_name,
      purchase_source = "pdp",
      type = "dropdown",
      component_id,
    } = data;

    const location = module_name ?? locationProp ?? "body atcarea";
    const productList = productsRaw || [];

    const defaultOption = productList[0]?.options?.[0];

    // Map through all products in the dropdown to create the items array
    const items = productList.map((product: AtcProduct) => {
      // Get base attributes (assuming this helper returns SKU, price, etc.)
      const item = getItemReportingAttributes({ type, ...product?.options?.[0] });
      item.purchase_source = purchase_source;
      item.source = purchase_source;
      item.list_type = type;
      return item;
    });

    segmentEvent("Product Dropdown Viewed", {
      location,
      list_type: type,
      type,
      product_line: getEventProductLine(productsRaw),
      purchase_type: defaultOption?.purchase_type,
      product_gender: defaultOption?.product_gender_new ?? defaultOption?.product_gender,
      core_category: defaultOption?.product_core_category,

      // The GA4 compliant array containing ALL products from the dropdown
      products: items,
      ...(component_id != null ? { component_id } : {}),
    });
  },

  onViewItemListOptions: (data: ManyOptionsData) => {
    const {
      options: optionsRaw,
      location: locationProp,
      module_name,
      purchase_source = "pdp",
      type = "dropdown_stickynav",
      component_id,
    } = data;

    const location = module_name ?? locationProp ?? "body atcarea";
    const options = optionsRaw || [];

    const defaultOption = options[0];

    // Map through all products in the dropdown options create the items array
    const items = options.map((option: GA4option) => {
      const item = getItemReportingAttributes({ type, ...option });
      const itemSource = option.purchase_source || purchase_source;
      item.purchase_source = itemSource;
      item.source = itemSource;
      item.list_type = type;
      return item;
    });

    segmentEvent("Product Dropdown Viewed", {
      // Top-level Segment Properties
      location,
      list_type: type,
      type,
      purchase_type: defaultOption?.purchase_type,
      product_gender: defaultOption?.product_gender_new ?? defaultOption?.product_gender,
      core_category: defaultOption?.product_core_category,

      // The GA4 compliant array containing ALL products from the dropdown
      products: items,
      ...(component_id != null ? { component_id } : {}),
    });
  },

  onProductSelected: (data: ProductSelection) => {
    const {
      option,
      location: locationInput,
      module_name,
      purchase_source: purchaseSourceInput,
      type = "button",
      ...rest
    } = data;

    const location =
      module_name ?? locationInput ?? rest.module_name ?? rest.location ?? "body atcarea";

    const purchase_source = purchaseSourceInput ?? rest.purchase_source ?? "pdp";
    const item = getItemReportingAttributes({ type, ...option });
    item.purchase_source = purchase_source as string;
    item.source = purchase_source as string;
    item.list_type = type;
    delete item.type;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { products: _ignoredCallerProducts, ...restForSegment } = rest;

    segmentEvent("Product Selected", {
      ...restForSegment,
      location: location as string,
      purchase_source,
      price: item?.price,
      shipping_interval: item?.shipping_interval,
      billing_interval: item?.billing_interval,
      type: type as string,
      name: option?.name,
      sku: option?.sku,
      purchase_type: option?.purchase_type,
      product_gender: option?.product_gender_new ?? option?.product_gender,
      product_category: option?.product_category,
      core_category: option?.product_core_category,
      image_url: option?.image?.url,
      source: purchase_source,
      products: [item],
    });
  },

  onProductViewed: (data: SingleProductData) => {
    const { product, option, location = "body atcarea", purchase_source = "pdp" } = data;
    const finalOption = option ?? product?.options?.[0];
    const item = getItemReportingAttributes(finalOption);

    item.purchase_source = purchase_source;
    item.source = purchase_source;
    delete item.type;

    segmentEvent("Product Viewed", {
      ...item,
      location,
      purchase_type: finalOption?.purchase_type,
      sku: finalOption?.sku,
      product_gender: finalOption?.product_gender_new ?? finalOption?.product_gender,
      core_category: finalOption?.product_core_category,
      image_url: finalOption?.image?.url,
      name: finalOption?.name,
      purchase_source,
      source: purchase_source,
      products: [item],
    });
  },

  onProductAdded: (data: ManyOptionsData) => {
    const {
      options: optionsInput = [],
      location,
      module_name,
      purchase_source: purchaseSourceInput,
      coupon: couponInput,
      type = "button",
      queryID,
      cartId = null,
      maskedId = null,
    } = data;

    const options = Array.isArray(optionsInput) ? optionsInput : [];
    const purchase_source = purchaseSourceInput ?? "pdp";

    const coupon = couponInput ?? data.coupon;

    const cartData = getCartStorage<CartData>(CART_STORAGE_KEYS.CART, "local");
    const { cart_id, masked_id } = cartData?.totals || {};

    const items = options.map((opt: GA4option) => {
      const item = getItemReportingAttributes({ type, ...opt });
      item.purchase_source = purchase_source;
      item.source = purchase_source;
      item.list_type = type;
      if (coupon) item.coupon = coupon;
      if (type) item.type = type;
      if (item.price == null && (opt?.price != null || opt?.value != null)) {
        item.price = opt.price ?? opt.value;
      }
      return item;
    });

    const defaultOption = options[0];

    segmentEvent("Product Added", {
      ...items[0],
      cart_id: cartId ?? cart_id,
      masked_id: maskedId ?? masked_id,
      location: module_name ?? location ?? "body atcarea",
      value:
        defaultOption?.bundled_price ||
        defaultOption?.old_price ||
        defaultOption?.price ||
        defaultOption?.new_price ||
        defaultOption?.ui_data?.price ||
        defaultOption?.value,
      queryID,
      text: data.text ?? "Add to cart",
      list_type: type,
      type,
      name: defaultOption?.name,
      purchase_type: defaultOption?.purchase_type,
      product_gender: defaultOption?.product_gender_new ?? defaultOption?.product_gender,
      core_category: defaultOption?.product_core_category,
      purchase_source,
      ...(coupon != null ? { coupon } : {}),
      products: items,
    });
  },

  //CART EVENTS

  onProductRemoved: (data: ManyOptionsData) => {
    const {
      options,
      location = "cart",
      purchase_source = "pdp",
      coupon = null,
      type = "dropdown",
      queryID = null,
      cartId = null,
      maskedId = null,
    } = data;

    const cartData = getCartStorage<CartData>(CART_STORAGE_KEYS.CART, "local");
    const { cart_id, masked_id } = cartData?.totals || {};
    const defaultOption = options[0];

    const item = getItemReportingAttributes({ type, ...defaultOption });
    item.purchase_source = purchase_source;
    item.source = purchase_source;
    delete item.type;

    segmentEvent("Product Removed", {
      ...item,
      cart_id: cartId ?? cart_id,
      masked_id: maskedId ?? masked_id,
      location,
      coupon,
      queryID,
      purchase_source,
      source: purchase_source,
      sku: defaultOption?.sku,
    });
  },

  onCartViewed: (data: CartData) => {
    const { items, totals } = data || {};
    const { masked_id, cart_id, coupon_code } = totals || {};

    const quote_id = getSafeItems(items)?.[0]?.quote?.quote_id;

    const products = getSegmentProducts(items || {}, coupon_code);
    const cart_state = getCartState(items);
    segmentEvent("Cart Viewed", {
      products,
      quote_id,
      business_line: getOrderCartBusinessLine(getSafeItems(items)),
      coupon: coupon_code,
      location: "cart",
      checkout_step_number: 1,
      checkout_step_name: "view cart",
      cart_value: totals?.subtotal,
      cart_id,
      masked_id,
      product_line: getOrderCartProductLine(getSafeItems(items)),
      cart_state,
      checkout_type: "standard",
      quiz_reco: getSegmentQuizRecoItems(items || {}),
      quote_url: `${process.env.NEXT_PUBLIC_MAGENTO_URL}quote/abandoned/index/?cart_id=${cart_id}`,
    });
  },

  onPromoCodeApplied: (data: CartTotalsData) => {
    data.coupon_amount =
      typeof data?.coupon_amount === "number" && data?.coupon_amount > 0
        ? +data?.coupon_amount.toFixed(2)
        : null;
    data.discount =
      typeof data?.discount === "number" && data?.discount > 0 ? -data?.discount.toFixed(2) : null;
    segmentEvent("Promo Code Applied", data);
  },

  onPromoCodeRemoved: (data: Partial<CartTotalsData>) => {
    segmentEvent("Promo Code Removed", data);
  },

  onCheckoutStarted: (data: Partial<CartFragment>, isGuest: boolean = false) => {
    const { items, prices } = data || {};
    const quote_id = readCookie("quote_id");
    const masked_id = readCookie("masked_id");
    const cart_id = readCookie("cart_id");

    const products = getSegmentCheckoutProducts(getSafeItems(items));
    const value =
      typeof prices?.grand_total?.value === "number"
        ? prices?.grand_total?.value.toFixed(4)
        : prices?.grand_total?.value;

    segmentEvent("Checkout Started", {
      products,
      value,
      business_line: getOrderCartBusinessLine(getSafeItems(items)),
      location: "checkout",
      checkout_step_number: 2,
      checkout_step_name: "begin checkout",
      cart_value: value,
      cart_id: cart_id ?? data.cart_id,
      /*
       * Registered Users: quote_id is a simple number (since an account only handles one active cart at a time).
       * Guest Users: quote_id is a long scrambled text string (hash). This is a security feature to prevent guest session hijacking.
       */
      quote_id: isGuest && masked_id ? masked_id : quote_id,
      product_line: getOrderCartProductLine(getSafeItems(items)),
      cart_state: getOrderCartState(getSafeItems(items)),
      checkout_type: "standard",
      path: "/checkout/",
      quiz_reco: getCheckoutQuizRecoItems(getSafeItems(items)),
      quote_url: `${process.env.NEXT_PUBLIC_MAGENTO_URL}quote/abandoned/index/?cart_id=${cart_id}`,
    });
  },

  onPaymentMethodDeclined: (
    data: Partial<PaymentMethodDeclined> & Pick<PaymentMethodDeclined, "failure_reason">
  ) => {
    const masked_id = readCookie("masked_id");
    const quote_id = readCookie("quote_id");
    const cart_id = readCookie("cart_id");
    const hasGatsbytoken = typeof window !== "undefined" && !!readCookie("gatsbytoken");
    const eventData = {
      ...data,
      ...{
        location: "checkout",
        checkout_type: "standard",
        page_type: "checkout",
        path: "/checkout/",
        cart_id,
        quote_id: !hasGatsbytoken && masked_id ? masked_id : quote_id,
      },
    };
    segmentEvent("Payment Method Declined", eventData as EventData);
  },

  onCheckoutShippingSubmitted: (data: CheckoutShippingSubmitted, isGuest: boolean = false) => {
    const { items, email, shipping_method, prices } = data;
    const masked_id = readCookie("masked_id");
    const quote_id = readCookie("quote_id");
    const cart_id = readCookie("cart_id");
    const products = getSegmentCheckoutProducts(getSafeItems(items));
    const { product_names, product_core_categories, product_image_urls } =
      getKlaviyoValues(products);

    segmentEvent("Checkout Shipping Submitted", {
      cart_value: prices?.grand_total?.value,
      products,
      product_names,
      product_core_categories,
      product_image_urls,
      cart_id,
      quote_id: isGuest && masked_id ? masked_id : quote_id,
      email,
      shipping_method,
      checkout_step_number: 3,
      checkout_step_name: "add_shipping_info",
      business_line: getOrderCartBusinessLine(getSafeItems(items)),
      product_line: getOrderCartProductLine(getSafeItems(items)),
      cart_state: getOrderCartState(getSafeItems(items)),
      checkout_type: "standard",
      location: "checkout",
      path: "/checkout/",
      quiz_reco: getCheckoutQuizRecoItems(getSafeItems(items)),
      quote_url: `${process.env.NEXT_PUBLIC_MAGENTO_URL}quote/abandoned/index/?cart_id=${cart_id}`,
    });
  },

  onCheckoutShippingUpdated: ((
    { items, prices },
    newMethodCode,
    priorMethodCode,
    isGuest = false
  ) => {
    if (!priorMethodCode || priorMethodCode === newMethodCode) return;
    const masked_id = readCookie("masked_id");
    const quote_id = readCookie("quote_id");
    const cart_id = readCookie("cart_id");

    segmentEvent("Checkout Shipping Updated", {
      cart_id: cart_id ?? "",
      quote_id: isGuest && masked_id ? masked_id : (quote_id ?? ""),
      cart_value: prices?.grand_total?.value ?? 0,
      cart_state: getOrderCartState(getSafeItems(items)),
      checkout_step_element: "shipping_tier",
      checkout_step_prior_value: priorMethodCode,
      checkout_type: "standard",
      location: "checkout",
      path: "/checkout/",
      product_line: getOrderCartProductLine(getSafeItems(items)),
      shipping_tier: newMethodCode,
    });
  }) as CheckoutShippingUpdatedFunction,

  onAddressValidationShown: (data: AddressValidationShown) => {
    segmentEvent("Address Validation Shown", data as unknown as EventData);
  },

  onEmailMarketingOptedIn: (data: EmailMarketingOptedIn) => {
    data.date_opted_in = new Date().toISOString();
    segmentEvent("Email Marketing Opted In", data as unknown as EventData);
  },

  // SEARCH EVENTS

  onSearchStarted: (data: SearchData) => {
    segmentEvent("Search Started", {
      ...data,
      search_type: null,
      search_filter: null,
    });
  },

  onSearchEntered: (data: SearchData) => {
    segmentEvent("Search Entered", {
      ...data,
      search_type: null,
      search_filter: null,
    });
  },

  onSearchResultsShown: (data: SearchData) => {
    segmentEvent("Search Results Shown", {
      ...data,
      search_type: null,
      search_filter: null,
    });
  },

  onSearchClicked: (data: SearchDataProduct) => {
    segmentEvent("Search Clicked", {
      ...data,
      search_result: null,
      search_type: null,
      search_filter: null,
    });
  },

  // ENGAGEMENT EVENTS

  onModalViewed: (data: ModalViewedData) => {
    segmentEvent("Modal Viewed", {
      content_type: null,
      creative_id: null,
      location: null,
      action: "click",
      text: "Learn More",
      name: "Modal",
      path: window?.location?.pathname,
      ...data,
    });
  },

  onVideoPlayed: (data: VideoPlayedData) => {
    segmentEvent("Video Played", {
      type: "button",
      ...data,
      video_provider: "Nutrafol",
      video_name: data.video_name || "Video",
    });
  },

  onCarouselClicked: (data: CarouselClickedData) => {
    segmentEvent("Carousel Clicked", {
      module_name: "Carousel",
      ...data,
    });
  },

  // NAVIGATION EVENTS

  onNavigationClicked: (data: NavigationClickedData) => {
    const { destination = "" } = data;
    const segments = destination?.split("/").filter(Boolean);
    segmentEvent("Navigation Clicked", {
      module_location: "navigation",
      top_category: segments[1] ?? null,
      sub_category: segments[2] ?? null,
      type: "link",
      text: "",
      location: data.module_location ?? "navigation",
      ...data,
    });
  },

  onOutboundLinkClicked: (data: NavigationClickedData) => {
    segmentEvent("Outbound Link Clicked", {
      type: "link",
      text: "",
      ...data,
    });
  },

  onMarketingCtaClicked: (data: MarketingCtaClickedData) => {
    segmentEvent("Marketing CTA Clicked", {
      text: "",
      ...data,
    });
  },
};

export type { GA4option, SingleOptionData, PaymentMethodDeclined } from "@/types/ga4";
